import { motion } from 'framer-motion';
import { Sparkles, User, Smile, Zap, Star, Heart } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export type StyleMode = 'real' | 'fun';
export type StylePreset = 'mini_chibi' | 'caricature' | 'emoji_energy';
export type FunLevel = 'low' | 'medium' | 'high';
export type Vibe = 'soft' | 'confident' | 'funny';

export interface StyleSettings {
  mode: StyleMode;
  preset: StylePreset;
  funLevel: FunLevel;
  vibe: Vibe;
  extras: {
    doodles: boolean;
    emojis: boolean;
    sparkles: boolean;
    arrows: boolean;
    miniCharacters: boolean;
  };
}

export const DEFAULT_STYLE_SETTINGS: StyleSettings = {
  mode: 'fun',
  preset: 'mini_chibi',
  funLevel: 'medium',
  vibe: 'soft',
  extras: { doodles: true, emojis: true, sparkles: true, arrows: true, miniCharacters: true },
};

const REAL_DEFAULTS: StyleSettings['extras'] = {
  doodles: false, emojis: false, sparkles: false, arrows: false, miniCharacters: false,
};

interface Props {
  settings: StyleSettings;
  onChange: (s: StyleSettings) => void;
}

export function AffirmationStyleControls({ settings, onChange }: Props) {
  const update = (patch: Partial<StyleSettings>) => onChange({ ...settings, ...patch });
  const updateExtras = (patch: Partial<StyleSettings['extras']>) =>
    onChange({ ...settings, extras: { ...settings.extras, ...patch } });

  const setMode = (mode: StyleMode) => {
    if (mode === 'real') {
      update({ mode, extras: { ...REAL_DEFAULTS, miniCharacters: false } });
    } else {
      update({ mode, extras: { doodles: true, emojis: true, sparkles: true, arrows: true, miniCharacters: true } });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Style Mode Toggle */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-foreground">Choose Your Style</p>
        <div className="grid grid-cols-2 gap-2">
          <StyleModeCard
            active={settings.mode === 'real'}
            icon={<User className="h-5 w-5" />}
            label="Real Me"
            desc="Polished & realistic"
            onClick={() => setMode('real')}
          />
          <StyleModeCard
            active={settings.mode === 'fun'}
            icon={<Sparkles className="h-5 w-5" />}
            label="Fun Character Me"
            desc="Stylized & playful"
            onClick={() => setMode('fun')}
          />
        </div>
      </div>

      {/* Fun Mode Options */}
      {settings.mode === 'fun' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {/* Style Presets */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-foreground">Style Preset</p>
            <div className="grid grid-cols-3 gap-2">
              <PresetCard
                active={settings.preset === 'mini_chibi'}
                icon={<Smile className="h-4 w-4" />}
                label="Mini Chibi"
                onClick={() => update({ preset: 'mini_chibi' })}
              />
              <PresetCard
                active={settings.preset === 'caricature'}
                icon={<Zap className="h-4 w-4" />}
                label="Caricature of Me"
                onClick={() => update({ preset: 'caricature' })}
              />
              <PresetCard
                active={settings.preset === 'emoji_energy'}
                icon={<Star className="h-4 w-4" />}
                label="Emoji Energy"
                onClick={() => update({ preset: 'emoji_energy' })}
              />
            </div>
          </div>

          {/* Fun Level */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-foreground">Fun Level</p>
            <div className="flex gap-2">
              {(['low', 'medium', 'high'] as FunLevel[]).map((level) => (
                <button
                  key={level}
                  onClick={() => update({ funLevel: level })}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all border ${
                    settings.funLevel === level
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted'
                  }`}
                >
                  {level === 'low' ? '😊 Low' : level === 'medium' ? '😄 Medium' : '🤪 High'}
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Vibe */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-foreground">Vibe</p>
        <div className="flex gap-2">
          {([
            { key: 'soft' as Vibe, label: '🌸 Soft & Uplifting' },
            { key: 'confident' as Vibe, label: '💪 Bold' },
            { key: 'funny' as Vibe, label: '😂 Playful' },
          ]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => update({ vibe: key })}
              className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all border ${
                settings.vibe === key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Extras Toggles */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-foreground">Extras</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 bg-muted/30 rounded-xl p-3">
          {([
            { key: 'doodles' as const, label: 'Doodles', modes: ['fun'] },
            { key: 'emojis' as const, label: 'Emojis', modes: ['fun'] },
            { key: 'sparkles' as const, label: 'Sparkles', modes: ['fun'] },
            { key: 'arrows' as const, label: 'Arrows', modes: ['fun'] },
            { key: 'miniCharacters' as const, label: 'Mini Chibis', modes: ['real', 'fun'] },
          ])
            .filter(({ modes }) => modes.includes(settings.mode))
            .map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-2">
              <Label htmlFor={`extra-${key}`} className="text-xs text-foreground cursor-pointer">{label}</Label>
              <Switch
                id={`extra-${key}`}
                checked={settings.extras[key]}
                onCheckedChange={(v) => updateExtras({ [key]: v })}
                className="scale-75"
              />
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function StyleModeCard({ active, icon, label, desc, onClick }: {
  active: boolean; icon: React.ReactNode; label: string; desc: string; onClick: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      className={`p-3 cursor-pointer transition-all text-center ${
        active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50'
      }`}
    >
      <div className={`mx-auto mb-1.5 ${active ? 'text-primary' : 'text-muted-foreground'}`}>{icon}</div>
      <p className="text-xs font-semibold text-foreground">{label}</p>
      <p className="text-[10px] text-muted-foreground">{desc}</p>
    </Card>
  );
}

function PresetCard({ active, icon, label, onClick }: {
  active: boolean; icon: React.ReactNode; label: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`p-2.5 rounded-xl text-center transition-all border ${
        active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-transparent bg-muted/50 hover:bg-muted'
      }`}
    >
      <div className={`mx-auto mb-1 ${active ? 'text-primary' : 'text-muted-foreground'}`}>{icon}</div>
      <p className="text-[10px] font-medium text-foreground">{label}</p>
    </button>
  );
}
